import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle, useMemo } from 'react'
import { useOutletContext, useLocation } from 'react-router-dom';
import { MapContainer, TileLayer, Marker } from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-cluster'
import { Select, FloatButton, Modal, notification, Input } from 'antd'
import './libs/Leaflet.ImageOverlay.Rotated.js'
import { TILE_URL } from "../config";
import EsriImageLayer from './EsriImageLayer';

import useBasicDevices from '../hooks/useBasicDevices'
import { apiFetch } from "../utils/api";
import useElements from '../hooks/useElements'
import { useDateRange } from '../hooks/useDateRange'
import { useDateRangeWrapper } from '../hooks/useDateRangeWrapper'
import { useSession } from '../context/SessionContext'
import RoseLegend from './RoseLegend';
import RoseMenu from './RoseMenu';
import PlumeLegend from './PlumeLegend';
import MapEventsLegend from './MapEventsLegend'
import MapControls from './MapControls';
import MapEventsFilterMenu from './MapEventsFilterMenu';
import PlumeTimeControls from './PlumeTimeControls';
import PlumeEmissionLegend from './PlumeEmissionLegend';
import { usePermissions } from '../hooks/usePermissions';
import { useGlobalLoading } from '../context/LoadingContext'

import { 
  createDeviceIcon, 
  createComplaintIcon, 
  createComplaintClusterIcon, 
  generateMapRoseIcon, 
  createIncidentIcon, 
  createIncidentClusterIcon,
  createEmissionPointIcon,
} from '../utils/leafletUtils'

import 'leaflet/dist/leaflet.css'
import '../styles/custom.css'

import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
import { formatBrowser } from '../utils/dateUtils';

export default forwardRef(function MapView({ onReady, onShowInfo, onSelectDevice, forceOpenPanel, onMapMove }, ref) {
  const { hasRole } = useSession()
  const { devices } = useBasicDevices();
  const devicesWithAnemometer = useMemo(
    () => devices.filter(d => d.hasAnemometer),
    [devices]
  );
  const { 
    handleOpenPlumePanel, plumePanelVisible, plumePanelCollapsed, setPlumePanelCollapsed, plumeMode, togglePlumeMode, complaints, fetchComplaints, incidents, fetchIncidents,  
    onShowComplaint, selectedComplaint, setSelectedComplaint, onShowIncident, selectedIncident, setSelectedIncident, selectedEmissionPointId, setSelectedEmissionPointId
  } = useOutletContext();
  const { startLoading, stopLoading } = useGlobalLoading()
  
  const [showComplaints, setShowComplaints] = useState(false)
  const [showIncidents, setShowIncidents] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [mapLayer, setMapLayer] = useState('streets')
  const [viewMode, setViewMode] = useState('devices')
  const [roseMarkers, setRoseMarkers] = useState([])
  
  const {
    canViewHistorical, isComplaintView, isIncidentView, canViewComplaints, canViewIncidents, canViewSidebar
  } = usePermissions();
  
  const plumePanelRef = useRef(null);
  const isMounted = useRef(true);

  // const [parameterOptions, setParameterOptions] = useState([])w
  const [periodicityRose, setPeriodicityRose] = useState('AVG5M') 
  const [showRoseMenu, setShowRoseMenu] = useState(false)
  const [selectedParam, setSelectedParam] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false);

  const periodPickerRef = useRef()

  const markerRefs = useRef({})
  const mapRef = useRef(null)


  // Plume Animation State
  const [clickPosition, setClickPosition] = useState(null);
  const [plumeLoading, setPlumeLoading] = useState(false);
  const [plumeTime, setPlumeTime] = useState('');
  const [isLegendVisible, setIsLegendVisible] = useState(false);
  const [plumeLegendData, setLegendData] = useState(null);
  const [plumeLegendUnit, setLegendUnit] = useState(null);
  const [plumeGas, setPlumeGas] = useState(null);
  const [tempPopup, setTempPopup] = useState(null);
  const isPlumeAnimating = isLegendVisible && plumeLegendData;
  const [plumeCalculatedEmissionId, setPlumeCalculatedEmissionId] = useState(null);

  // Plume Animation Refs
  const plumeFrames = useRef([]);
  const currentFrameIndex = useRef(0);
  const animationInterval = useRef(null);
  const currentOverlay = useRef(null);
  const plumeMarkerPoint = useRef(null);

  const anemometerDevices = devicesWithAnemometer.map(d => ({ id: d.id, name: d.name }));
  const [selectedAnemometerId, setSelectedAnemometerId] = useState(null);
  // const [selectedComplaint, setSelectedComplaint] = useState(null)
  const [editing, setEditing] = useState(false)
  const [breakPoints, setBreakPoints] = useState(null)
  const [roseLegendData, setRoseLegendData] = useState(null);
  const [emissionPoints, setEmissionPoints] = useState([]);

  const location = useLocation();

  const [pendingCenter, setPendingCenter] = useState(false);

  const [customRange, setCustomRange] = useState({
    from: dayjs().startOf('day'), // 24 horas previas, comenzando desde el minuto 0
    to: dayjs().endOf('day') // El final del día actual
  })

  const {
    showCustom,
    selectedLabel,
    setPreset,
    setShowCustom,
    selectedPreset,
    presetDefs,
  } = useDateRange(setCustomRange);

  const {
    elements: parameterOptions,
    loading: elemsLoading,
    error: elemsError
  } = useElements([], true);

  const showRoseLegend = viewMode === 'roses' && Array.isArray(roseLegendData) && roseLegendData.length > 0;

  const [incidentFilter, setIncidentFilter] = useState({ status0: true, status1: true });
  const [complaintFilter, setComplaintFilter] = useState({ status0: true, status1: true });

  // const [showIncidentsFilter, setShowIncidentsFilter] = useState(false);
  // const [showComplaintsFilter, setShowComplaintsFilter] = useState(false);
  const [filterComplaintsOpen, setFilterComplaintsOpen] = useState(false);
  const [filterIncidentsOpen, setFilterIncidentsOpen] = useState(false);
  const showEventsFilter = filterComplaintsOpen || filterIncidentsOpen;

  const incidentsRange = useDateRangeWrapper();
  const complaintsRange = useDateRangeWrapper();

  const incidentPeriodPickerRef = useRef();
  const complaintPeriodPickerRef = useRef();

  const isIncidentInRange = (incident) => {
    const { from, to } = incidentsRange.dateRange;
    if (!from || !to || !incident.date) return true;

    const incidentDate = dayjs(incident.date, 'YYYY-MM-DD HH:mm');
    if (!incidentDate.isValid()) return false;

    return (
      incidentDate.isSameOrAfter(from) && incidentDate.isSameOrBefore(to)
    );
  };

  const toggleComplaintsFilter = () => {
    if (!showComplaints) return; // seguridad
    setFilterComplaintsOpen(v => !v);
  };

  const toggleIncidentsFilter = () => {
    if (!showIncidents) return;
    setFilterIncidentsOpen(v => !v);
  };

  const toggleComplaints = () => {
    setShowComplaints(prev => {
      const next = !prev;

      if (next) {
        // si se activan complaints → abre su filtro y cierra el otro
        // setFilterComplaintsOpen(true);
        // setFilterIncidentsOpen(false);
      } else {
        // si se apagan → cierra su filtro
        setFilterComplaintsOpen(false);
      }

      return next;
    });
  };

  const toggleIncidents = () => {
    setShowIncidents(prev => {
      const next = !prev;

      if (next) {
        // setFilterIncidentsOpen(true);
        // setFilterComplaintsOpen(false);
      } else {
        setFilterIncidentsOpen(false);
      }

      return next;
    });
  };

  const isComplaintInRange = (complaint) => {
    const { from, to } = complaintsRange.dateRange;
    if (!from || !to || !complaint.date) return true;

    const complaintDate = dayjs(complaint.date, 'YYYY-MM-DD HH:mm');
    if (!complaintDate.isValid()) return false;

    return (
      complaintDate.isSameOrAfter(from) && complaintDate.isSameOrBefore(to)
    );
  };

  const filteredIncidents = useMemo(() => {
    return incidents.filter(i => {
      if (i.status === 0 && !incidentFilter.status0) return false;
      if (i.status === 1 && !incidentFilter.status1) return false;
      if (!isIncidentInRange(i)) return false;
      return true;
    });
  }, [incidents, incidentFilter, incidentsRange.dateRange]);

  const filteredComplaints = useMemo(() => {
    return complaints.filter(i => {
      if (i.status === 0 && !complaintFilter.status0) return false;
      if (i.status === 1 && !complaintFilter.status1) return false;
      if (!isComplaintInRange(i)) return false;
      return true;
    });
  }, [complaints, complaintFilter, complaintsRange.dateRange]);

  useEffect(() => {
    return () => {
      stopPlayback();
    };
  }, []);

  useEffect(() => {
    return () => {
      isMounted.current = false;
      stopPlayback();   // muy importante
    };
  }, []);

  useEffect(() => {
      // console.log('Hook disparado: pathname:', location.pathname, 'devices:', devices.length, 'mapRef:', !!mapRef.current);
    if (location.pathname === '/map' && devices.length && mapRef.current) {
      centerMap();
    }
  }, [location.pathname, devices, mapRef.current]);

  useEffect(() => {
    if (location.pathname === '/map' && devices.length && !mapRef.current) {
      setPendingCenter(true);
    }
  }, [location.pathname, devices]);

  useEffect(() => {
    if (
      (location.pathname === '/map') &&
      devices.length &&
      mapRef.current &&
      (pendingCenter || !pendingCenter) // por si quieres centrar siempre, o solo cuando pending
    ) {
      centerMap();
      setPendingCenter(false); // resetea
    }
  }, [mapRef.current, devices, location.pathname, pendingCenter]);

  useEffect(() => {
    if (!isComplaintView || !mapRef.current || !complaints?.length) return;

    const validCoords = complaints
      .filter(c => c.latitude && c.longitude)
      .map(c => [Number(c.latitude), Number(c.longitude)]);

    if (validCoords.length === 1) {
      mapRef.current.setView(validCoords[0], 16);
    } else if (validCoords.length > 1) {
      mapRef.current.fitBounds(validCoords, { padding: [30, 30], maxZoom: 17 });
    }
  }, [isComplaintView, complaints]);

  useEffect(() => {
    if (selectedId && markerRefs.current[selectedId]) {
      markerRefs.current[selectedId].openPopup()
    }
  }, [selectedId]);

  useEffect(() => {
    const loadEmissionPoints = async () => {
      try {
        const res = await apiFetch("/api/emission-points", { method: "GET" });
        if (!res.ok) throw new Error();
        const data = await res.json();
        setEmissionPoints(Array.isArray(data) ? data : []);
      } catch {
        setEmissionPoints([]);
      }
    };

    loadEmissionPoints();
  }, []);

  useImperativeHandle(ref, () => ({
    flyToMarker: (lat, lng, id) => {
      if (!mapRef.current || !mapRef.current._loaded) return;

      const center = mapRef.current.getCenter();
      const tol = 0.0002;

      if (
        Math.abs(center.lat - lat) > tol ||
        Math.abs(center.lng - lng) > tol
      ) {
        mapRef.current.panTo([lat, lng], {
          animate: true,
          duration: 0.5
        });
      }

      setSelectedId(id);
    },
    centerMarkers: centerMap,
    setEmissionMarker: (lat, lng, name = "Emission source") => {
      if (!mapRef.current || !mapRef.current._loaded) return;

      if (plumeMarkerPoint.current && mapRef.current.hasLayer(plumeMarkerPoint.current)) {
        mapRef.current.removeLayer(plumeMarkerPoint.current);
        plumeMarkerPoint.current = null;
      }

      plumeMarkerPoint.current = L.circleMarker([lat, lng], {
        color: '#7a0403',
        radius: 4,
        fillColor: '#005199',
        fillOpacity: 1
      }).addTo(mapRef.current)
      .bindPopup(name)
      .openPopup();
    },
    handlePlumeSubmit,
    setClickPosition: (pos) => {
      setClickPosition(pos);
    },
    cleanUpPlume
  }))

  useEffect(() => {
    if (!mapRef.current || !onMapMove) return;
    const handler = () => {
      const center = mapRef.current.getCenter();
      onMapMove({ lat: center.lat, lng: center.lng });
    };
    mapRef.current.on('moveend', handler);
    return () => {
      mapRef.current.off('moveend', handler);
    };
  }, [onMapMove]);

  useEffect(() => {
    if (location.pathname !== '/map') {
      cleanUpPlume();
      stopPlayback();
    }
    // return () => {
    //   cleanUpPlume();
    // };
  }, [location.pathname]);

  const centerMap = () => {
    if (!mapRef.current) {
      // console.log("No mapRef.current disponible");
      return;
    }
    // Filtra solo devices con coordenadas válidas:
    const bounds = devices
      .filter(d => 
        typeof d.latitude === 'number' && 
        typeof d.longitude === 'number' && 
        !isNaN(d.latitude) && 
        !isNaN(d.longitude)
      )
      .map(d => [d.latitude, d.longitude]);
    // console.log("Centrando mapa a bounds:", bounds);

    if (bounds.length > 1) {
      mapRef.current.fitBounds(bounds, { padding: [30, 30], maxZoom: 17 });
    } else if (bounds.length === 1) {
      mapRef.current.setView(bounds[0], 16);
    } else {
      // console.log("No hay dispositivos con coordenadas válidas para centrar el mapa.");
    }
  };

  const fetchAndGenerateRoses = async () => {
    if (!devices.length || !selectedParam || !periodPickerRef.current) return false;

    const { from, to } = periodPickerRef.current.getRange();
    startLoading();

    try {
      const response = await apiFetch('/api/wind-graphs/pollution-rose', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deviceIds: devices.map(d => d.id),
          elementId: selectedParam,
          startDate: from.toISOString(),
          endDate: to.toISOString(),
          type: 'pollution-rose',
          anemometerDeviceId: selectedAnemometerId,
          periodicity: periodicityRose,
          breaks: breakPoints
        }),
      });

      if (!response || !response.ok) {
        throw new Error(`HTTP ${response?.status}`);
      }

      const data = await response.json();

      if (!Array.isArray(data) || data.length === 0) {
        showNoDataNotification();
        setRoseMarkers([]);
        setRoseLegendData(null);
        return false;
      }

      const markers = data
        .filter(d =>
          typeof d.latitude === 'number' &&
          typeof d.longitude === 'number'
        )
        .map(d => ({
          id: d.id,
          position: [d.latitude, d.longitude],
          icon: generateMapRoseIcon(
            d.data,
            d.name || d.serialNumber,
            d.mean,
            d.calm
          )
        }));

      const firstWithData = data.find(
        d => Array.isArray(d.data) && d.data.length > 0
      );

      if (!firstWithData) {
        showNoDataNotification();
        setRoseMarkers([]);
        setRoseLegendData(null);
        return false;
      }

      setRoseMarkers(markers);
      setRoseLegendData(firstWithData.data);
      setViewMode('roses');

      if (markers.length && mapRef.current) {
        const bounds = markers.map(m => m.position);
        mapRef.current.fitBounds(bounds, {
          padding: [30, 30],
          maxZoom: 17
        });
      }

      return true; // 👈 importante

    } catch (err) {
      setRoseMarkers([]);
      showNoDataNotification();
      return false;
    } finally {
      stopLoading();
    }
  };

  const showNoDataNotification = () => {
    notification.warning({
      message: 'No data available',
      description: 'There is no data to display for the selected parameter and period.',
      placement: 'topRight',
      duration: 4
    });
  };

  // Clean up plume resources
  function cleanUpPlume(opts = {}) {
    const {
      keepMarker = false,
      keepPopup = false,
      keepLegend = false,
      keepEmissionPoints = true
    } = opts;

    stopPlayback();

    if (currentOverlay.current && mapRef.current) {
      mapRef.current.removeLayer(currentOverlay.current);
      currentOverlay.current = null;
    }

    if (!keepMarker && plumeMarkerPoint.current && mapRef.current) {
      mapRef.current.removeLayer(plumeMarkerPoint.current);
      plumeMarkerPoint.current = null;
    }

    if (!keepPopup && tempPopup && mapRef.current) {
      mapRef.current.closePopup(tempPopup);
      setTempPopup(null);
    }

    plumeFrames.current = [];
    currentFrameIndex.current = 0;
    setPlumeTime('');

    if (!keepLegend) {
      setIsLegendVisible(false);
    }

    if (!keepEmissionPoints) {
      setSelectedEmissionPointId(null);
      setPlumeCalculatedEmissionId(null);
    }
  }


  // Show specific plume frame
  const showFrame = (index) => {
    if (!isMounted.current || !mapRef.current) return;
    const frames = plumeFrames.current;
    if (!frames || index >= frames.length) return;

    const frame = frames[index];
    const isValid = frame?.image_base64 && frame?.image_bounds?.nw;

    // Remove previous overlay
    if (currentOverlay.current && mapRef.current) {
      mapRef.current.removeLayer(currentOverlay.current);
      currentOverlay.current = null;
    }

    // Close previous popup if exists
    if (tempPopup && mapRef.current) {
      mapRef.current.closePopup(tempPopup);
      setTempPopup(null);
    }

    if (!isValid) {
      const hora = frame?.timestamp ? formatBrowser(frame.timestamp.replace(' ', 'T') + "Z") : "unknown time";
      let message = `No plume visible (${hora}) - Wind threshold not exceeded`;

      if (frame?.err_code === "wind_direction_too_variable") {
        message = `Wind direction too variable (${hora})`;
      }

      const popup = L.popup({ autoClose: false, closeButton: false })
        .setLatLng([clickPosition.lat, clickPosition.lng])
        .setContent(message)
        .openOn(mapRef.current);
      
      setTempPopup(popup);

      // Auto-close after 3 seconds
      setTimeout(() => {
        if (mapRef.current && popup && mapRef.current.hasLayer(popup)) {
          mapRef.current.closePopup(popup);
          if (tempPopup === popup) {
            setTempPopup(null);
          }
        }
      }, 3000);
    } else {
      const imageUrl = `data:image/png;base64,${frame.image_base64}`;
      const overlay = L.imageOverlay.rotated(
        imageUrl,
        L.latLng(...frame.image_bounds.nw),
        L.latLng(...frame.image_bounds.ne),
        L.latLng(...frame.image_bounds.sw),
        { opacity: 0.70, interactive: false }
      ).addTo(mapRef.current);
      currentOverlay.current = overlay;
    }

    // Update timestamp
    setPlumeTime(frame?.timestamp ? `Date time: ${formatBrowser(frame.timestamp.replace(' ', 'T') + "Z")}` : '');
    currentFrameIndex.current = index;
  };

  // Animate plume frames
  const animatePlume = (frames, centerLat, centerLon) => {
    if (!isMounted.current || !mapRef.current) return;
    cleanUpPlume();

    if (!frames || frames.length === 0) return;

    const allEmpty = frames.every(f => 
      !f?.image_base64 || !f?.image_bounds?.nw || !f?.image_bounds?.ne || !f?.image_bounds?.sw
    );

    if (allEmpty) {
      Modal.error({
        title: 'Plume cannot be displayed',
        content: 'The wind threshold was not exceeded at any time'
      });
      return;
    }

    plumeFrames.current = frames;
    currentFrameIndex.current = 0;

    // Add emission marker
    plumeMarkerPoint.current = L.circleMarker([clickPosition.lat, clickPosition.lng], {
      color: '#7a0403',
      radius: 3,
      fillColor: '#005199',
      fillOpacity: 1
    }).addTo(mapRef.current).bindPopup("Emission source");

    // Center map
    // mapRef.current.setView([centerLat, centerLon], 16);
    const currentCenter = mapRef.current.getCenter();
    if (
      Math.abs(currentCenter.lat - centerLat) > 0.00001 ||
      Math.abs(currentCenter.lng - centerLon) > 0.00001
    ) {
      mapRef.current.panTo([centerLat, centerLon]);
    }

    // Show first frame immediately
    showFrame(0);
    startPlayback(3000);
    setIsLegendVisible(true);
  };

  function startPlayback(delayMs = 3000) {
    // si ya está corriendo, no hagas nada
    if (animationInterval.current) return;
    setIsPlaying(true);
    const tick = () => {
      if (!isMounted.current || !mapRef.current) {
        stopPlayback();
        return;
      }

      const frames = plumeFrames.current;
      if (!frames?.length) return;

      const next = (currentFrameIndex.current + 1) % frames.length;
      showFrame(next);

      animationInterval.current = setTimeout(tick, delayMs);
    };
    animationInterval.current = setTimeout(tick, delayMs);
  }

  function stopPlayback() {
    if (animationInterval.current) {
      clearTimeout(animationInterval.current);
      animationInterval.current = null;
    }
    setIsPlaying(false);
  }

  const handlePlumeSubmit = async (values) => {
    setPlumeCalculatedEmissionId(null);
    cleanUpPlume({ keepMarker: true, keepPopup: true });

    if (values.metodo === 'manual') {
      values.temp = values.temp === null ? undefined : values.temp;
      values.presion = values.presion === null ? undefined : values.presion;
      values.viento = values.viento === null ? undefined : values.viento;
      values.windVane = values.windVane === null ? undefined : values.windVane;
      values.radiacionSolar = values.radiacionSolar === null ? undefined : values.radiacionSolar;
      values.nublado = values.nublado ? "yes" : "no";
    }

    let fechaInicioObj, fechaFinObj = null;

    const combineDateTime = (date, time) => {
      if (!date || !time) return null;
      return date.set('hour', time.hour()).set('minute', time.minute()).set('second', 0);
    };

    if (values.tipoTiempo === 'rango') {
      fechaInicioObj = combineDateTime(values.startDate, values.startTime);
      // Calcula la fecha de fin sumando los minutos de duración
      fechaFinObj = fechaInicioObj.add(values.duration, 'minute');

      const diffHours = fechaFinObj.diff(fechaInicioObj, 'hour', true);
      if (diffHours > 2) {
        Modal.error({ title: 'Error', content: 'The range cannot exceed 2 hours' });
        return;
      }
    } else {
      fechaInicioObj = combineDateTime(values.exactDate, values.exactTime);
    }

    if (values.emissionRate < 0.01 && values.minPpm > 0.01) {
      Modal.error({ 
        title: 'Error', 
        content: 'With emissionRate < 0.01, minPpm must be ≤ 0.01' 
      });
      return;
    }

    const datos = {
      on_premise: "true",
      gas: values.gas,
      emission_rate: +values.emissionRate,
      // start_date: fechaInicioObj.format('YYYY-MM-DD HH:mm:ss'),
      start_date: fechaInicioObj.toISOString(),
      // end_date: fechaFinObj.format('YYYY-MM-DD HH:mm:ss'),
      source_height: +values.sourceHeight,
      altura_concentracion: +values.alturaConcentracion,
      min_ppm: +values.minPpm,
      umbral_viento: +values.umbralViento,
      center_lat: clickPosition.lat,
      center_lon: clickPosition.lng,
      deviceIds: [],
      tempExt: null,
      pressure: null,
      windSpeed: null,
      windVane: null,
      solarRad: null,
      isCloudy: null,
      callOpenMeteo: false
    };

    // Añadir fecha de fin
    if (values.tipoTiempo === 'rango') {
        datos.end_date = fechaFinObj.toISOString();
    }

    if (values.metodo === 'dispositivo') {
      datos.deviceIds = [+values.dispositivos];
      datos.solarRad = values.radiacionSolar === "" ? null : +values.radiacionSolar;
      datos.isCloudy = values.nublado === "yes" ? true : (values.nublado === "no" ? false : null);
    } else if (values.metodo === 'manual') {
      datos.tempExt = values.temp === '' ? null : +values.temp;
      datos.pressure = values.presion === '' ? null : +values.presion;
      datos.windSpeed = values.viento === '' ? null : +values.viento;
      datos.windVane = values.windVane === '' ? null : +values.windVane;
      datos.solarRad = values.radiacionSolar === '' ? null : +values.radiacionSolar;
      datos.isCloudy = values.nublado === "yes";
    }

    try {
      startLoading();
      const response = await apiFetch("/api/wind-graphs/plume", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(datos),
      });

      const res = await response.json();

      if (response.status === 409) {
        Modal.info({
          title: 'Calculation in progress',
          content: 'Another plume calculation is already running. Please wait for it to finish.'
        });
        return;
      } else if (response.status === 422) {
        Modal.info({
          title: 'Plume not available',
          content: res.message
        });
        return;
      } else if (response.status === 500) {
        Modal.error({
          title: 'Server error',
          content: res.error ||'An unexpected error ocurred while calculating the plume'
        });
        return;
      }

      if (res.frames?.length) {
        setLegendUnit(res.unit);
        setPlumeGas(values.gas);
        setPlumePanelCollapsed(true);
        setPlumeCalculatedEmissionId(selectedEmissionPointId);
        animatePlume(res.frames, res.center_lat, res.center_lon);
        if (res.levels) {
          const sorted = Object.entries(res.levels)
            .map(([v, color]) => ({
              value: parseFloat(v),
              color
            }))
            .sort((a, b) => a.value - b.value);

            const minPPM = Number(values.minPpm);
            const minValue = res.unit === 'ppb' ? minPPM * 1000 : minPPM;

            const ranges = sorted.map((curr, idx) => {
              const prev = sorted[idx - 1];
              return {
                from: prev ? prev.value : minValue,
                to: curr.value,
                color: curr.color
              };
            })
            .filter(r => r.to > r.from);

          setLegendData(ranges);
        }
      } else {
        Modal.info({
          title: 'Plume not available',
          content: 'The wind did not reach the minimum required speed during the selected period.'
        });
      }
    } catch (error) {
      Modal.error({
        title: 'Error',
        content: `Request error: ${error.message}`
      });
    } finally {
      stopLoading();
    }
  };

  return (
    <div style={{ height: '100%', width: '100%', position: 'relative' }}>
      
      <MapContainer
        center={[25.23293, 55.30577]}
        zoom={13}
        minZoom={12}
        maxZoom={19}
        style={{ height: '100%', width: '100%' }}
        zoomControl={false}
        ref={el => { if (el && !mapRef.current) { mapRef.current = el; onReady?.() } }}
      >
        {mapLayer === 'streets' && (
          <TileLayer
            url={TILE_URL}
            attribution="&copy; OpenStreetMap contributors"
            maxNativeZoom={19}
            maxZoom={19}
          />
        )}
        {mapLayer === 'satellite' && <EsriImageLayer />}

        <MapControls
          canViewHistorical={canViewHistorical}
          isComplaintView={isComplaintView}
          isIncidentView={isIncidentView}
          canViewComplaints={canViewComplaints}
          canViewIncidents={canViewIncidents}

          viewMode={viewMode}
          showComplaints={showComplaints}
          showIncidents={showIncidents}
          plumePanelVisible={plumePanelVisible}
          filterComplaintsOpen={filterComplaintsOpen}
          filterIncidentsOpen={filterIncidentsOpen}

          setViewMode={setViewMode}
          onOpenComplaintsFilter={toggleComplaintsFilter}
          onOpenIncidentsFilter={toggleIncidentsFilter}
          onToggleComplaints={toggleComplaints}
          onToggleIncidents={toggleIncidents}
          setShowRoseMenu={setShowRoseMenu}

          centerMap={centerMap}
          cleanUpPlume={cleanUpPlume}
          togglePlumeMode={togglePlumeMode}
          plumeMode={plumeMode}

          mapLayer={mapLayer}
          setMapLayer={setMapLayer}
        />
        {viewMode === 'devices' && canViewSidebar && (
          <MarkerClusterGroup>
            {devices.map(device => (
              <Marker
                key={device.id}
                position={[device.latitude, device.longitude]}
                icon={createDeviceIcon(device)}
                eventHandlers={{
                  click: () => {
                    if (onShowInfo) onShowInfo(device);
                    if (forceOpenPanel) forceOpenPanel(device.id);
                    setSelectedComplaint(null)
                    setSelectedIncident(null)
                  },
                  dblclick: () => {
                    if (onSelectDevice) onSelectDevice(device);
                    if (forceOpenPanel) forceOpenPanel(device.id);
                    setSelectedComplaint(null)
                    setSelectedIncident(null)
                  }
                }}
                ref={el => { if (el) markerRefs.current[device.id] = el }}
              />
            ))}
          </MarkerClusterGroup>
        )}

        {viewMode === 'roses' && Array.isArray(roseMarkers) && roseMarkers.length > 0 && (
          <MarkerClusterGroup>
            {roseMarkers.map(marker => {
              const device = devices.find(d => d.id === marker.id);
              const deviceName = device ? device.name : marker.id;
              return (
                <Marker
                  key={marker.id}
                  position={marker.position}
                  icon={marker.icon}
                  title={deviceName}
                  eventHandlers={{
                    click: () => {
                      if (onShowInfo && device) onShowInfo(device);
                      if (forceOpenPanel) forceOpenPanel(marker.id);
                      setSelectedComplaint(null)
                      setSelectedIncident(null)
                    },
                    dblclick: () => {
                      if (onSelectDevice && device) onSelectDevice(device);
                      if (forceOpenPanel) forceOpenPanel(marker.id);
                      setSelectedComplaint(null)
                      setSelectedIncident(null)
                    }
                  }}
                  ref={el => { if (el) markerRefs.current[marker.id] = el }}
                />
              )
            })}
          </MarkerClusterGroup>
        )}


        {(showComplaints || isComplaintView) && (
          <MarkerClusterGroup iconCreateFunction={createComplaintClusterIcon}>
            {filteredComplaints.map(c => (
              <Marker
                key={`complaint-${c.id}-${c.status}`}
                position={[Number(c.latitude), Number(c.longitude)]}
                icon={createComplaintIcon(c.status)}
                status={c.status} 
                eventHandlers={{
                  click: () => onShowComplaint?.(c)
                }}
              />
            ))}
          </MarkerClusterGroup>
        )}

        {(showIncidents || isIncidentView) && (
          <MarkerClusterGroup iconCreateFunction={createIncidentClusterIcon}>
            {filteredIncidents.map(i => (
              <Marker
                key={`incident-${i.id}-${i.status}`}
                position={[Number(i.latitude), Number(i.longitude)]}
                icon={createIncidentIcon(i.status)}
                status={i.status}
                eventHandlers={{
                  click: () => onShowIncident?.(i)
                }}
              />
            ))}
          </MarkerClusterGroup>
        )}

        {plumeMode && emissionPoints.map(p => {
          let mode = "normal";
          if (p.id === plumeCalculatedEmissionId) {
            mode = "active";
          } else if (p.id === selectedEmissionPointId) {
            mode = "selected";
          }

          return (
            <Marker
              key={`emission-${p.id}`}
              position={[Number(p.latitude), Number(p.longitude)]}
              icon={createEmissionPointIcon(mode)}
              eventHandlers={{
                click: () => {
                  setSelectedEmissionPointId(p.id);

                  if (!plumePanelVisible) {
                    handleOpenPlumePanel();
                  }
                  if (plumePanelCollapsed) {
                    setPlumePanelCollapsed(false);
                  }

                  if (mapRef.current) {
                    mapRef.current.setView(
                      [Number(p.latitude), Number(p.longitude)],
                      17
                    );
                  }
                }
              }}
              zIndexOffset={1000}
            />
          );
        })}
      </MapContainer>

      <div id="map-overlay-left-root" className="map-overlay-left">
        {/* FILA 1 → ROSE */}
        {(showRoseMenu || showRoseLegend) && (
          <div className="map-overlay-block">
            <div className="map-overlay-left-row">
              {showRoseMenu && (
                <RoseMenu
                  parameterOptions={parameterOptions}
                  selectedParam={selectedParam}
                  setSelectedParam={setSelectedParam}
                  periodicityRose={periodicityRose}
                  setPeriodicityRose={setPeriodicityRose}
                  anemometerDevices={anemometerDevices}
                  selectedAnemometerId={selectedAnemometerId}
                  setSelectedAnemometerId={setSelectedAnemometerId}
                  periodPickerRef={periodPickerRef}
                  customRange={customRange}
                  setCustomRange={setCustomRange}
                  showCustom={showCustom}
                  setShowCustom={setShowCustom}
                  setPreset={setPreset}
                  selectedPreset={selectedPreset}
                  selectedLabel={selectedLabel}
                  presetDefs={presetDefs}
                  breakPoints={breakPoints}
                  setBreakPoints={setBreakPoints}
                  fetchAndGenerateRoses={fetchAndGenerateRoses}
                  onClose={() => setShowRoseMenu(false)}
                  mapRef={mapRef}
                />
              )}
              {viewMode === 'roses' && showRoseLegend && (
                <RoseLegend
                  data={roseLegendData}
                  selectedParam={selectedParam}
                  parameterOptions={parameterOptions}
                  periodicityRose={periodicityRose}
                  customRange={customRange}
                />
              )}
            </div>
          </div>
        )}
        {/* FILA 2 → EVENTS */}
        {(showEventsFilter || showComplaints || showIncidents || isComplaintView || isIncidentView) && (
          <div className="map-overlay-block">
            <div className="map-overlay-left-row">
              {showEventsFilter && (showComplaints || showIncidents) && (
                <MapEventsFilterMenu
                  showComplaints={showComplaints}
                  showIncidents={showIncidents}
                  filterComplaintsOpen={filterComplaintsOpen}
                  filterIncidentsOpen={filterIncidentsOpen}
                  complaintFilter={complaintFilter}
                  setComplaintFilter={setComplaintFilter}
                  complaintsRange={complaintsRange}
                  complaintPeriodPickerRef={complaintPeriodPickerRef}
                  incidentFilter={incidentFilter}
                  setIncidentFilter={setIncidentFilter}
                  incidentsRange={incidentsRange}
                  incidentPeriodPickerRef={incidentPeriodPickerRef}
                />
              )}
              {(showComplaints || isComplaintView || showIncidents || isIncidentView) && (
                <MapEventsLegend
                  showComplaints={showComplaints || isComplaintView}
                  showIncidents={showIncidents || isIncidentView}
                  createComplaintIcon={createComplaintIcon}
                  createIncidentIcon={createIncidentIcon}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* BOTTOM AREA */}
      {plumeMode && (
        <>
          {/* TOP RIGHT – Plume Legend */}
          {isLegendVisible && plumeLegendData && (
            <div
              className="map-overlay-right"
              style={{ right: plumePanelCollapsed ? 10 : 410 }}
            >
              <PlumeLegend data={plumeLegendData} gas={plumeGas} unit={plumeLegendUnit} />
            </div>
          )}

          {/* BOTTOM RIGHT – Time + Emission aligned */}
          <div
            className="map-overlay-bottom"
            style={{ right: plumePanelCollapsed ? 10 : 410 }}
          >
            {plumeTime && (
              <PlumeTimeControls
                plumeTime={plumeTime}
                isPlaying={isPlaying}
                stopPlayback={stopPlayback}
                startPlayback={startPlayback}
                showFrame={showFrame}
                plumeFrames={plumeFrames}
                currentFrameIndex={currentFrameIndex}
              />
            )}

            <PlumeEmissionLegend />
          </div>
        </>
      )}
    </div>
  )
})
